import { useState } from 'react';
import {
  Badge,
  Button,
  Drawer,
  Group,
  Paper,
  ScrollArea,
  Stack,
  Switch,
  Table,
  Text,
  Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { IconAdjustments, IconUsers } from '@tabler/icons-react';
import type { AdminUserRow, ContentKind, ContentOption } from '@creativitas/shared';
import { formatDaysLeft } from '@creativitas/shared';
import { PageHeader } from '@/components/ui/PageHeader';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/StateViews';
import { UserChip } from '@/components/domain/ParticipantChip';
import { toUserMessage } from '@/lib/api';
import { SESSION_QUERY_KEY, AVAILABILITY_QUERY_KEY } from '@/features/auth/useSession';
import {
  ADMIN_CONTENT_KEY,
  ADMIN_USERS_KEY,
  confirmEmail,
  fetchAdminContent,
  fetchAdminUsers,
  setGameOwnership,
  setManualAccess,
  setTrialExemption,
} from './api';
import { EntitlementHistory, PlanSummary, SubscriptionSection, UserOrders } from './SubscriptionAdmin';
import { UserPrivacyAdmin } from './UserPrivacyAdmin';

/**
 * Admin user-access screen (§48).
 *
 * Everything the owner needs to test the access model by hand: who has what,
 * and one switch per entitlement. Subscription and game ownership stay visibly
 * separate concepts (§13).
 */

const KIND_LABELS: Record<ContentKind, string> = {
  deck: 'Колода',
  spread: 'МАК-сессия',
  training_material: 'Обучение',
  game: 'Игра',
};

/**
 * Неопубликованное пользователю не откроется и с точечным доступом: выдать его
 * можно заранее, но заработает он только после публикации.
 */
const UNPUBLISHED_LABELS: Record<string, string> = {
  draft: 'черновик',
  archived: 'в архиве',
};

const formatDate = (iso: string | null): string =>
  iso ? new Date(iso).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—';

export function AdminUsersPage() {
  const [editing, setEditing] = useState<AdminUserRow | null>(null);

  const usersQuery = useQuery({ queryKey: ADMIN_USERS_KEY, queryFn: fetchAdminUsers });
  /*
    Список материалов живой: администратор заводит колоду или обучение и сразу
    идёт выдавать к ним доступ. Общие 30 секунд staleTime показали бы список
    без только что созданного, поэтому при каждом заходе он запрашивается заново.
  */
  const contentQuery = useQuery({
    queryKey: ADMIN_CONTENT_KEY,
    queryFn: fetchAdminContent,
    staleTime: 0,
    refetchOnMount: 'always',
  });

  const rows = usersQuery.data?.items ?? [];
  // Keep the drawer in sync after a mutation refetches the list.
  const current = editing ? (rows.find((row) => row.user.id === editing.user.id) ?? editing) : null;

  return (
    <>
      <PageHeader
        eyebrow="Администрирование"
        title="Пользователи"
        description="Пробный период, тарифы Лайт и Про, владение играми и точечный доступ. Оплата пока не подключена — тариф выдаётся здесь вручную."
      />

      {usersQuery.isLoading ? (
        <LoadingState label="Загружаем пользователей..." />
      ) : usersQuery.isError ? (
        <ErrorState onRetry={() => void usersQuery.refetch()} />
      ) : rows.length === 0 ? (
        <EmptyState icon={<IconUsers size={28} stroke={1.5} />} title="Пользователей пока нет" />
      ) : (
        <Paper withBorder radius="lg" style={{ borderColor: 'var(--border-subtle)' }}>
          <ScrollArea>
            <Table verticalSpacing="sm" horizontalSpacing="md" miw={900}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Пользователь</Table.Th>
                  <Table.Th>Регистрация</Table.Th>
                  <Table.Th>Пробный период</Table.Th>
                  <Table.Th>Тариф</Table.Th>
                  <Table.Th>Игры</Table.Th>
                  <Table.Th>Ручной доступ</Table.Th>
                  <Table.Th />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {rows.map((row) => (
                  <Table.Tr key={row.user.id}>
                    <Table.Td>
                      <Group gap="sm" wrap="nowrap">
                        <UserChip name={row.user.displayName} secondary={row.user.email} size="sm" />
                        {row.user.role === 'admin' ? (
                          <Badge size="xs" variant="light" color="gold">
                            админ
                          </Badge>
                        ) : null}
                      </Group>
                    </Table.Td>

                    <Table.Td>
                      <Stack gap={2}>
                        <Text size="sm" c="var(--text-muted)">
                          {formatDate(row.user.createdAt)}
                        </Text>
                        {!row.user.emailVerified ? (
                          <Text size="xs" c="red.7" lh={1.3}>
                            почта не подтверждена
                          </Text>
                        ) : null}
                      </Stack>
                    </Table.Td>

                    <Table.Td>
                      {row.access.trial.exempt ? (
                        <Badge variant="light" color="gold" size="sm">
                          не применяется
                        </Badge>
                      ) : !row.access.trial.started ? (
                        <Badge variant="default" size="sm">
                          не начинался
                        </Badge>
                      ) : row.access.trial.active ? (
                        <Badge variant="light" color="teal" size="sm">
                          {formatDaysLeft(row.access.trial.daysLeft ?? 0)}
                        </Badge>
                      ) : (
                        <Text size="sm" c="var(--text-muted)">
                          закончился {formatDate(row.access.trial.endsAt)}
                        </Text>
                      )}
                    </Table.Td>

                    <Table.Td>
                      <PlanSummary subscription={row.access.subscription} />
                    </Table.Td>

                    <Table.Td>
                      {row.access.ownedGames.length === 0 ? (
                        <Text size="sm" c="var(--text-subtle)">
                          —
                        </Text>
                      ) : (
                        <Stack gap={2}>
                          {row.access.ownedGames.map((game) => (
                            <Text key={game.gameId} size="sm">
                              {game.title}
                            </Text>
                          ))}
                        </Stack>
                      )}
                    </Table.Td>

                    <Table.Td>
                      {row.access.manualGrants.length === 0 ? (
                        <Text size="sm" c="var(--text-subtle)">
                          —
                        </Text>
                      ) : (
                        <Badge variant="light" size="sm">
                          {row.access.manualGrants.length}
                        </Badge>
                      )}
                    </Table.Td>

                    <Table.Td>
                      <Button
                        size="xs"
                        variant="light"
                        leftSection={<IconAdjustments size={14} />}
                        onClick={() => setEditing(row)}
                      >
                        Доступ
                      </Button>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </ScrollArea>
        </Paper>
      )}

      <AccessDrawer
        row={current}
        content={contentQuery.data?.items ?? []}
        onClose={() => setEditing(null)}
      />
    </>
  );
}

/* -------------------------------------------------------------------------- */

function AccessDrawer({
  row,
  content,
  onClose,
}: {
  row: AdminUserRow | null;
  content: ContentOption[];
  onClose: () => void;
}) {
  const queryClient = useQueryClient();

  const afterChange = (message: string) => {
    void queryClient.invalidateQueries({ queryKey: ADMIN_USERS_KEY });
    // The edited user may be the admin themselves, or the demo may have both
    // windows open — refresh the current session's access too.
    void queryClient.invalidateQueries({ queryKey: SESSION_QUERY_KEY });
    void queryClient.invalidateQueries({ queryKey: AVAILABILITY_QUERY_KEY });
    notifications.show({ message, color: 'teal' });
  };

  const onError = (error: unknown) => {
    notifications.show({ title: 'Не удалось изменить доступ', message: toUserMessage(error), color: 'red' });
  };

  const confirmEmailMutation = useMutation({
    mutationFn: (userId: string) => confirmEmail(userId),
    onSuccess: () => afterChange('Почта подтверждена — теперь можно войти'),
    onError,
  });

  const trialMutation = useMutation({
    mutationFn: ({ userId, exempt }: { userId: string; exempt: boolean }) =>
      setTrialExemption(userId, { exempt }),
    onSuccess: (_data, variables) =>
      afterChange(
        variables.exempt ? 'Пробный период больше не применяется' : 'Пробный период применяется',
      ),
    onError,
  });

  const ownershipMutation = useMutation({
    mutationFn: ({ userId, gameId, granted }: { userId: string; gameId: string; granted: boolean }) =>
      setGameOwnership(userId, { gameId, granted }),
    onSuccess: (_data, variables) =>
      afterChange(variables.granted ? 'Игра передана во владение' : 'Владение игрой снято'),
    onError,
  });

  const manualMutation = useMutation({
    mutationFn: ({
      userId,
      contentKind,
      contentId,
      granted,
    }: {
      userId: string;
      contentKind: ContentKind;
      contentId: string;
      granted: boolean;
    }) => setManualAccess(userId, { contentKind, contentId, granted }),
    onSuccess: (_data, variables) =>
      afterChange(variables.granted ? 'Доступ выдан' : 'Доступ снят'),
    onError,
  });

  /*
    Владеют полной игрой; демо — отдельная запись, и точечно её открывают
    ручным правом, как колоду или расклад. Владение «демо» ничего бы не дало:
    его правило ссылается на владение полной игрой.
  */
  const games = content.filter((item) => item.kind === 'game' && item.variant !== 'demo');
  const grantable = content.filter((item) => item.kind !== 'game' || item.variant === 'demo');

  const busy =
    confirmEmailMutation.isPending ||
    trialMutation.isPending ||
    ownershipMutation.isPending ||
    manualMutation.isPending;

  return (
    <Drawer
      opened={row !== null}
      onClose={onClose}
      title={row ? `Доступ: ${row.user.displayName}` : ''}
      size="lg"
    >
      {row ? (
        <Stack gap="xl">
          {/*
            Неподтверждённая почта запирает вход целиком, поэтому, когда она есть,
            она выше всего остального: без неё прочие переключатели ничего не дают.
          */}
          {!row.user.emailVerified ? (
            <Stack gap="xs">
              <Title order={5}>Почта не подтверждена</Title>
              <Text size="sm" c="var(--text-muted)">
                Пока адрес не подтверждён, войти нельзя. Если письмо до человека не
                доходит — например, почтовик отклоняет его как спам, — подтвердите
                адрес вручную. Делайте это, только если знаете, что ящик его.
              </Text>
              <Group>
                <Button
                  size="xs"
                  variant="light"
                  disabled={busy}
                  loading={confirmEmailMutation.isPending}
                  onClick={() => confirmEmailMutation.mutate(row.user.id)}
                >
                  Подтвердить почту вручную
                </Button>
              </Group>
            </Stack>
          ) : null}

          {/*
            Пробный период стоит первым: это ответ на вопрос «почему у купившего
            идёт обратный отсчёт». Отдельный переключатель, а не часть подписки —
            владение игрой бессрочно и подписки не требует (§13).
          */}
          <Stack gap="xs">
            <Title order={5}>Пробный период</Title>
            <Text size="sm" c="var(--text-muted)">
              {row.user.role === 'admin'
                ? 'Администратору пробный период не применяется — доступ у него полный по роли.'
                : 'Отсчёт нужен тому, кто ещё ничего не приобрёл. Купившему его лучше выключить: доступ у него бессрочный, а обратный отсчёт читается как срок.'}
            </Text>
            <Switch
              checked={!row.access.trial.exempt}
              disabled={busy || row.user.role === 'admin'}
              aria-label="Пробный период"
              label={row.access.trial.exempt ? 'Не применяется' : 'Применяется'}
              onChange={(event) =>
                trialMutation.mutate({
                  userId: row.user.id,
                  exempt: !event.currentTarget.checked,
                })
              }
            />
          </Stack>

          <SubscriptionSection row={row} busy={busy} onChanged={afterChange} onError={onError} />

          <Stack gap="xs">
            <Title order={5}>Владение играми</Title>
            <Text size="sm" c="var(--text-muted)">
              Бессрочное право вести конкретную игру. Тариф для этого не нужен.
            </Text>
            <Stack gap="xs">
              {games.map((game) => {
                const owned = row.access.ownedGames.some((item) => item.gameId === game.id);
                return (
                  <Switch
                    key={game.id}
                    checked={owned}
                    disabled={busy}
                    aria-label={`Владение игрой: ${game.title}`}
                    label={game.title}
                    onChange={(event) =>
                      ownershipMutation.mutate({
                        userId: row.user.id,
                        gameId: game.id,
                        granted: event.currentTarget.checked,
                      })
                    }
                  />
                );
              })}
            </Stack>
          </Stack>

          <Stack gap="xs">
            <Title order={5}>Точечный доступ</Title>
            <Text size="sm" c="var(--text-muted)">
              Открывает один материал независимо от тарифа и пробного периода.
              Новые колоды, МАК-сессии и обучение появляются в списке сами; черновик
              откроется пользователю после публикации.
            </Text>
            <Stack gap="xs">
              {grantable.map((item) => {
                const granted = row.access.manualGrants.some(
                  (grant) => grant.contentId === item.id,
                );
                const unpublished = UNPUBLISHED_LABELS[item.status];
                return (
                  <Group key={item.id} justify="space-between" wrap="nowrap" gap="sm">
                    <Group gap="xs" wrap="nowrap" style={{ minWidth: 0 }}>
                      <Badge size="xs" variant="default">
                        {KIND_LABELS[item.kind]}
                      </Badge>
                      <Text size="sm" truncate>
                        {item.title}
                      </Text>
                      {unpublished ? (
                        <Badge size="xs" variant="light" color="gray" style={{ flexShrink: 0 }}>
                          {unpublished}
                        </Badge>
                      ) : null}
                    </Group>
                    <Switch
                      checked={granted}
                      disabled={busy}
                      aria-label={`Точечный доступ: ${item.title}`}
                      onChange={(event) =>
                        manualMutation.mutate({
                          userId: row.user.id,
                          contentKind: item.kind,
                          contentId: item.id,
                          granted: event.currentTarget.checked,
                        })
                      }
                    />
                  </Group>
                );
              })}
            </Stack>
          </Stack>

          <UserOrders userId={row.user.id} />

          <EntitlementHistory row={row} busy={busy} onChanged={afterChange} onError={onError} />

          {/* Последним: удаление — самое редкое и самое необратимое действие здесь. */}
          <UserPrivacyAdmin row={row} onErased={onClose} />
        </Stack>
      ) : null}
    </Drawer>
  );
}
